import { useId, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as RPNInput from "react-phone-number-input";

import { LoaderCircle } from "~/components/icons";
import { Button } from "@workspace/web-ui/components/button";
import { Checkbox } from "@workspace/web-ui/components/checkbox";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@workspace/web-ui/components/dialog";
import { Input } from "@workspace/web-ui/components/input";
import { Label } from "@workspace/web-ui/components/label";
import { cn } from "@workspace/web-ui/lib/utils";
import { genders, states } from "../../../data";

import { useAuthModal } from "../pages/welcome/store/use-auth-modal";
import useRegisterFormStore from "../pages/welcome/store/use-register-form-store";
import { useRegisterModal } from "../pages/welcome/store/use-register-modal";
import { FormSelect } from "../../../components/form-select";
import { CountrySelect, FlagComponent, PhoneInput } from "./phone-input";
import { useRegisterMutation } from "~/features/auth/api/mutations";
import { RegisterFormSchema, RegisterFormData } from "~/features/auth/schemas/auth.schema";


export function RegisterForm() {
  const id = useId();
  const { setStep, setEmail } = useRegisterFormStore();
  const { open: openAuthModal } = useAuthModal();
  const { close } = useRegisterModal();
  const [errorMessage, setErrorMessage] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterFormSchema),
  });

  const calculateMaxDate = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  function handleLogin() {
    close();
    openAuthModal();
  }

  const { mutate: createPatient, isPending } = useRegisterMutation()


  const handleFormSubmit = (data: RegisterFormData) => {
    setErrorMessage("");

    createPatient(
      {
        user_details: {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          phone_number: data.phone_number,
          date_of_birth: data.date_of_birth,
          gender: data.gender,
          city: data.city || undefined,
          state: data.state,
          terms: data.terms,
        },
      },
      {
        onSuccess: (response) => {
          if (!response.success) {
            if (
              response?.error &&
              response?.error?.includes("Email already registered")
            ) {
              setErrorMessage(
                "An account exists with this email, try logging in.",
              );
            }
            return;
          }
          setEmail(data.email);
          setStep(2);

        },
        onError: (error) => {
          console.log("Error", error);
        },
      },
    );
  };

  return (
    <DialogContent className="hide-scrollbar h-[calc(100vh-40px)] max-w-xs overflow-y-scroll rounded-[24px] px-4 pb-10 pt-6 sm:max-w-sm sm:px-8 sm:pb-10 sm:pt-8 md:max-w-xl lg:px-[64px] lg:pb-[60px] lg:pt-[40px] xl:max-w-[700px]">
      <div className="flex flex-col items-center gap-2">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold sm:text-3xl lg:text-[40px]">
            Register Now
          </DialogTitle>
          <DialogDescription className="text-center text-base lg:text-lg">
            Let&apos;s create an account for you, before you continue.
          </DialogDescription>
        </DialogHeader>
      </div>

      {errorMessage && (
        <div className="w-full rounded-md bg-red-50 p-3 text-center">
          <p className="text-sm text-red-800">
            {errorMessage}{" "}
            <button
              type="button"
              onClick={handleLogin}
              className="font-medium text-blue-600 underline hover:text-blue-800 hover:no-underline"
            >
              Login here
            </button>
          </p>
        </div>
      )}

      <form
        className="space-y-4 lg:space-y-5"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className={cn("relative w-full rounded-lg bg-background")}>
              <Label htmlFor={`${id}-first-name`}>First Name</Label>
              <Input
                {...register("first_name")}
                id={`${id}-first-name`}
                type="text"
              />
              {errors.first_name && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.first_name.message}
                </p>
              )}
            </div>

            <div className={cn("relative w-full rounded-lg bg-background")}>
              <Label htmlFor={`${id}-last-name`}>Last Name</Label>
              <Input
                {...register("last_name")}
                id={`${id}-last-name`}
                type="text"
              />
              {errors.last_name && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.last_name.message}
                </p>
              )}
            </div>
          </div>

          <div className={cn("relative w-full rounded-lg bg-background")}>
            <Label htmlFor={`${id}-email`}>Email address</Label>
            <Input
              {...register("email")}
              id={`${id}-email`}
              type="email"
              placeholder="Email"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-500">
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="w-full">
              <Label htmlFor={`${id}-state`}>State</Label>
              <select
                {...register("state")}
                id={`${id}-state`}
                className="flex h-9 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm shadow-black/5 transition-shadow placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {states.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.label}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.state.message}
                </p>
              )}
            </div>

            <div className="w-full">
              <Label htmlFor={`${id}-phone`}>Phone</Label>
              <Controller
                control={control}
                name="phone_number"
                render={({ field }) => (
                  <RPNInput.default
                    className="shadow-xs flex rounded-md"
                    international
                    flagComponent={FlagComponent}
                    countrySelectComponent={CountrySelect}
                    defaultCountry="US"
                    inputComponent={PhoneInput}
                    id={id}
                    placeholder="Enter phone number"
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue ?? "")}
                  />
                )}
              />
              {errors.phone_number && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.phone_number.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="w-full">
              <div className="group relative">
                <Label htmlFor={`${id}-date-of-birth`}>Date of Birth</Label>
                <Input
                  {...register("date_of_birth")}
                  id={`${id}-date-of-birth`}
                  placeholder="Date of birth"
                  type="date"
                  max={calculateMaxDate()}
                  className="h-9 w-full rounded-[8px] transition-all duration-200 group-focus-within:h-9 group-focus-within:pt-2 has-[input:not(:placeholder-shown)]:h-9 has-[input:not(:placeholder-shown)]:pt-2"
                />
                {errors.date_of_birth && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.date_of_birth.message}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full">
              <Label htmlFor={`${id}-gender`}>Gender</Label>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <FormSelect
                    label="Gender"
                    items={genders}
                    selectedValue={field.value}
                    onSelect={field.onChange}
                    showSearch={false}
                  />
                )}
              />
              {errors.gender && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Controller
            control={control}
            name="terms"
            render={({ field }) => (
              <Checkbox
                id={`${id}-terms`}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <Label htmlFor={`${id}-terms`} className="text-xs text-[#7F8493]">
            I agree to the{" "}
            <a
              className="font-medium text-[#363840] underline hover:no-underline"
              href="/terms"
              target="_blank"
            >
              Terms of service
            </a>{" "}
            and{" "}
            <a
              className="font-medium text-[#363840] underline hover:no-underline"
              href="/privacy-policy"
              target="_blank"
            >
              Privacy policy
            </a>
          </Label>
        </div>
        {errors.terms && (
          <p className="text-xs text-red-500">{errors.terms.message}</p>
        )}

        <Button
          type="submit"
          className="h-12 sm:h-[50px] xl:h-[57px]"
          fullWidth
          size="xl"
          disabled={isPending}
        >
          {isPending && (
            <LoaderCircle
              className="-ms-1 me-2 animate-spin"
              size={16}
              strokeWidth={2}
              aria-hidden="true"
            />
          )}
          Register
        </Button>
      </form>

      <p className="text-center text-xs text-[#7F8493] xl:text-base">
        Already have an account?{" "}
        <button
          type="button"
          className="font-semibold text-[#4272DD] underline hover:no-underline"
          onClick={handleLogin}
          disabled={isPending}
        >
          Log in
        </button>
      </p>
    </DialogContent>
  );
}
